import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  List,
  Button,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNotifications } from '../../hooks/api/notificationsApiHooks';
import { NotificationType, Notification } from '../../services/api/notifications/notificationsApi';
import NotificationItem from './NotificationItem';

interface NotificationModalProps {
  open: boolean;
  onClose: () => void;
  onMarkAsRead: (notificationId: number) => void;
  onMarkAsUnread: (notificationId: number) => void;
  onMarkAllAsRead: () => void;
  onRefresh: () => void;
}


/**
 * Modal para visualizar todas as notificações
 * Permite filtrar, pesquisar e gerenciar notificações
 */
const NotificationModal: React.FC<NotificationModalProps> = ({
  open,
  onClose,
  onMarkAsRead,
  onMarkAsUnread,
  onMarkAllAsRead,
  onRefresh
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Configurações das abas
  const tabs = [
    { label: 'Todas', value: 'all', queryParams: {} },
    { label: 'Não lidas', value: 'unread', queryParams: { onlyUnread: true } },
    { label: 'Atividades', value: 'projects', queryParams: { types: ['PROJECT_ASSIGNED', 'PROJECT_AUTO_CREATED'] as NotificationType[] } },
    { label: 'Checklists Pendentes', value: 'checklists', queryParams: { types: ['COMMENT_ADDED'] as NotificationType[] } },
    { label: 'Pagamentos', value: 'payments', queryParams: { types: ['PAYMENT_RECEIVED', 'PAYMENT_OVERDUE'] as NotificationType[] } }
  ];

  // Query para as notificações baseada na aba atual
  const currentTabConfig = tabs[currentTab];
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch
  } = useNotifications({
    limit: 50,
    ...currentTabConfig.queryParams
  });

  // Verificar se é notificação de checklist pendente
  const isChecklistNotification = (notification: Notification) => {
    return notification.type === 'COMMENT_ADDED' && 
           (notification.title.toLowerCase().includes('checklist') || 
            notification.message.toLowerCase().includes('checklist'));
  };

  // Filtrar notificações apenas por busca
  const filteredNotifications = notifications.filter(notification =>
    searchTerm === '' ||
    notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    notification.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setSearchTerm(''); // Limpar busca ao trocar aba
  };

  const handleRefresh = () => {
    refetch();
    onRefresh();
  };

  const handleMarkAllAsRead = () => {
    onMarkAllAsRead();
    // Aguardar um pouco e fazer refetch para atualizar a lista
    setTimeout(() => {
      refetch();
    }, 500);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '80vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h5" fontWeight="bold">
              Notificações
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} não lidas`}
                color="error"
                size="small"
              />
            )}
          </Box>
          
          <Box>
            <IconButton onClick={handleRefresh} title="Atualizar">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} title="Fechar">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="notification tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 'medium'
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Search and Actions */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar notificações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
            sx={{ flexGrow: 1 }}
          />
          
          {unreadCount > 0 && (
            <Button
              variant="outlined"
              startIcon={<MarkEmailReadIcon />}
              onClick={handleMarkAllAsRead}
              size="small"
            >
              Marcar todas como lidas
            </Button>
          )}
        </Box>
      </Box>

      {/* Content */}
      <DialogContent sx={{ flexGrow: 1, p: 0, overflow: 'auto' }}>
        {error ? (
          <Box p={3}>
            <Alert severity="error">
              Erro ao carregar notificações. Tente novamente.
            </Alert>
          </Box>
        ) : isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            color="text.secondary"
          >
            <Typography variant="h6" gutterBottom>
              {searchTerm ? 'Nenhuma notificação encontrada' : 'Nenhuma notificação'}
            </Typography>
            <Typography variant="body2">
              {searchTerm 
                ? 'Tente ajustar os termos de busca'
                : currentTab === 1 
                  ? 'Todas as notificações foram lidas'
                  : 'Não há notificações para exibir'
              }
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={onMarkAsRead}
                  onMarkAsUnread={onMarkAsUnread}
                  onClose={onClose}
                />
                {index < filteredNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          {filteredNotifications.length > 0 && (
            `${filteredNotifications.length} notificaç${filteredNotifications.length > 1 ? 'ões' : 'ão'} encontrada${filteredNotifications.length > 1 ? 's' : ''}`
          )}
        </Typography>
        <Button onClick={onClose} variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationModal;
